import {
  gql, useMutation, useQuery,
} from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  useState, useEffect,
  useContext,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  DragDropContext, Draggable, Droppable, DroppableProvided,
} from 'react-beautiful-dnd';
import { Typography, Box } from '../../../../1-primative';
import {
  Card, CardContent, Button,
} from '../../../../2-component';
import { NewHomePageWidget } from './newHomePageWidget';
import { HomePageWidget } from './homePageWidget';
import { SettingsTitle } from '../../../../3-pattern';
import { useThemeTokens } from '../../../../../providers/themeTokenProvider';
import { UserContext } from '../../../../../providers/userContextProvider';

const FETCH_HOME_PAGE_CONFIGURATION = gql`
  query fetchHomePageConfiguration($homePageConfigurationId: ObjectID!) {
    fetchHomePageConfiguration(homePageConfigurationId: $homePageConfigurationId) {
      homePageConfiguration {
        id
        translatedName {
          en
          fr
        }
        widgets {
          id
          type
          options
        }
        options
      }
    }
  }
`;

const UPDATE_HOME_PAGE_CONFIGURATION = gql`
  mutation updateHomePageConfiguration($input: UpdateHomePageConfigurationInput!) {
    updateHomePageConfiguration(input: $input) {
      homePageConfiguration {
        id
      }
    }
  }
`;

export const HomePageConfigurationEditor = () => {
  const { homePageConfigurationId } = useParams();
  const { t } = useTranslation(['homePageConfiguration', 'settings']);
  const [widgets, setWidgets] = useState<any[]>([]);
  const [homePageConfiguration, setHomePageConfiguration] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [activeWidget, setActiveWidget] = useState<any>({ type: '', options: {} });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const { sys } = useThemeTokens();
  const { userContext } = useContext(UserContext);

  const { loading, error, data } = useQuery(FETCH_HOME_PAGE_CONFIGURATION, {
    variables: { homePageConfigurationId },
    skip: !homePageConfigurationId,
  });

  const [updateHomePageConfiguration] = useMutation(UPDATE_HOME_PAGE_CONFIGURATION);

  useEffect(() => {
    if (data?.fetchHomePageConfiguration?.homePageConfiguration) {
      setHomePageConfiguration(data.fetchHomePageConfiguration.homePageConfiguration);
      setWidgets(data.fetchHomePageConfiguration.homePageConfiguration.widgets || []);
    }
  }, [data]);

  // Drag and drop styles
  const getItemStyle = (_isDragging: boolean, draggableStyle: any) => ({
    marginBottom: '20px',
    ...draggableStyle,
  });

  const getListStyle = (_isDraggingOver: boolean) => ({
    background: sys.color.background,
    width: '100%',
    borderRadius: '4px',
    marginTop: '16px',
    padding: '8px',
  });

  // Drag and drop handler
  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const newWidgets = [...widgets];
    const [movedItem] = newWidgets.splice(result.source.index, 1);
    newWidgets.splice(result.destination.index, 0, movedItem);
    setWidgets(newWidgets);
  };

  const saveChanges = async () => {
    await updateHomePageConfiguration({
      variables: {
        input: {
          homePageConfigurationId,
          widgets: widgets.map((widget) => ({
            type: widget.type,
            options: widget.options,
          })),
        },
      },
    });
  };

  const removeWidget = (i: number) => {
    const updatedWidgets = [...widgets];
    updatedWidgets.splice(i, 1);
    setWidgets(updatedWidgets);
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>Error loading home page configuration</Typography>;
  if (!homePageConfiguration) return <Typography>Home page configuration not found</Typography>;

  return (
    <>
      <Card>
        <CardContent>
          <SettingsTitle
            title={homePageConfiguration.translatedName?.en || homePageConfiguration.translatedName?.fr}
            description={t('homePageConfiguration:editor.description')}
          />
          <Box display='flex' flexDirection='row' justifyContent='end' sx={{ mt: 3, width: '100%' }}>
            <Button onClick={saveChanges} variant="filled" label={t('homePageConfiguration:editor.saveChanges')} />
          </Box>
        </CardContent>
      </Card>
      <Box sx={{
        display: 'flex', gap: 2, flexDirection: 'column', mt: 2,
      }}>
        <Typography data-testid='client-name-title' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} variant='displayLargeVariant'>
          {`${t('homePageConfiguration:welcome')}, ${userContext.firstName}`}
        </Typography>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="home-page-widgets">
            {(provided: DroppableProvided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
              >
                {widgets.map((item: any, i: number) => (
                  <Draggable
                    key={item.id || `widget-${i}`}
                    draggableId={item.id || `widget-${i}`}
                    index={i}
                  >
                    {(dragProvided: any, dragSnapshot: any) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        style={getItemStyle(
                          dragSnapshot.isDragging,
                          dragProvided.draggableProps.style,
                        )}
                      >
                        <HomePageWidget
                          type={item.type}
                          i={i}
                          onDelete={removeWidget}
                          onEdit={() => {
                            setActiveIndex(i);
                            setActiveWidget(item);
                            setOpen(true);
                          }}
                          options={item.options || {}}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      <Box display='flex' flexDirection='row' justifyContent='end' sx={{ mt: 3, width: '100%' }}>
        <Button onClick={() => {
          setActiveWidget({ type: '', options: {} });
          setOpen(true);
        }} label={t('homePageConfiguration:editor.addWidget')} variant='tonal' />
        <NewHomePageWidget
          setWidgets={setWidgets}
          widgets={widgets}
          open={open}
          setOpen={setOpen}
          activeWidget={activeWidget}
          index={activeIndex ?? undefined}
          onSave={() => {
            setActiveIndex(null);
            setActiveWidget({ type: '', options: {} });
          }}
        />
      </Box>
    </>
  );
};
